import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, Edit2, Trash2, X, Save } from 'lucide-react';
import AgendaDiaryCard, { fileToDataUrl } from '../components/AgendaDiaryCard';
import AgendaProgressBar from '../components/AgendaProgressBar';
import { requestFcmToken } from '../firebaseMessaging';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lumis_user_token')}` });

const STATUS_LABEL = {
  pendente: { label: 'Pendente', color: 'bg-gray-100 text-gray-600' },
  em_andamento: { label: 'Em andamento', color: 'bg-blue-100 text-blue-700' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  atrasada: { label: 'Atrasada', color: 'bg-red-100 text-red-700' },
  cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-400' },
};
const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };
const CATEGORIA_LABEL = {
  trabalho: 'Trabalho', saude: 'Saúde', financeiro: 'Financeiro', familia: 'Família', estudo: 'Estudos',
  desenvolvimento_pessoal: 'Desenvolvimento Pessoal', exercicios: 'Exercícios', compras: 'Compras', casa: 'Casa',
  viagens: 'Viagens', eventos: 'Eventos', lazer: 'Lazer', alimentacao: 'Alimentação', transporte: 'Transporte',
  networking: 'Networking', contatos: 'Contatos', documentacao: 'Documentação', bancos: 'Bancos',
  investimentos: 'Investimentos', projetos: 'Projetos', marketing: 'Marketing', vendas: 'Vendas',
  atendimento: 'Atendimento', recursos_humanos: 'Recursos Humanos', juridico: 'Jurídico', tecnologia: 'Tecnologia',
  manutencao: 'Manutenção', pets: 'Pets', espiritualidade: 'Espiritualidade', voluntariado: 'Voluntariado',
  aniversarios: 'Aniversários', metas: 'Metas', planejamento: 'Planejamento', tarefas: 'Tarefas',
  lembretes: 'Lembretes', importante: 'Importante', pessoal: 'Pessoal', outros: 'Outros',
};
const ANTECEDENCIA_LABEL = {
  no_momento: 'No momento do compromisso',
  '15min': '15 minutos antes',
  '30min': '30 minutos antes',
  '1h': '1 hora antes',
  '2h': '2 horas antes',
  '1dia': '1 dia antes',
};
const ANTECEDENCIA_LABEL_2 = Object.fromEntries(Object.entries(ANTECEDENCIA_LABEL).filter(([k]) => k !== '1dia'));

const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const fmtDateTime = (s) => { if (!s) return '—'; return new Date(s).toLocaleString('pt-BR'); };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function AgendaAtividadeDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [diary, setDiary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [diaryTemplate, setDiaryTemplate] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(res => setTrialInfo(res.data))
      .catch(() => {});
  }, []);

  const loadActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agenda/activities/${id}`, { headers: authHeaders() });
      setActivity(res.data);
    } catch {
      toast.error('Atividade não encontrada');
      navigate('/agenda');
    }
  }, [id, navigate]);

  const loadDiary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agenda/activities/${id}/diary`, { params: { limit: 7 }, headers: authHeaders() });
      setDiary(res.data);
      setHasMore(res.data.length >= 7);
    } catch {
      toast.error('Erro ao carregar diário');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadActivity(), loadDiary()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMore = async () => {
    if (!diary.length) return;
    setLoadingMore(true);
    try {
      const before = diary[0].data_referencia;
      const res = await axios.get(`${API}/agenda/activities/${id}/diary`, { params: { limit: 7, before }, headers: authHeaders() });
      setDiary(prev => [...res.data, ...prev]);
      setHasMore(res.data.length >= 7);
    } catch {
      toast.error('Erro ao carregar mais registros');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSaveDiary = async (dataRef, conteudo) => {
    try {
      const res = await axios.put(`${API}/agenda/activities/${id}/diary/${dataRef}`, { conteudo }, { headers: authHeaders() });
      setDiary(prev => prev.map(e => e.data_referencia === dataRef ? res.data : e));
      toast.success('Registro salvo!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar registro');
      throw err;
    }
  };

  const handleUpload = async (dataRef, files) => {
    try {
      const attachments = await Promise.all(files.map(async f => ({
        data_url: await fileToDataUrl(f), nome_original: f.name, tipo_mime: f.type,
      })));
      const res = await axios.post(`${API}/agenda/activities/${id}/diary/${dataRef}/attachments`, attachments, { headers: authHeaders() });
      setDiary(prev => prev.map(e => e.data_referencia === dataRef ? res.data : e));
      toast.success('Anexo(s) adicionado(s)!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar anexo');
      throw err;
    }
  };

  const handleComplete = async () => {
    try {
      await axios.put(`${API}/agenda/activities/${id}/complete`, {}, { headers: authHeaders() });
      toast.success('Atividade concluída!');
      setShowConfirmComplete(false);
      loadActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao concluir atividade');
    }
  };

  const handleProgressChange = async (value) => {
    try {
      const res = await axios.put(`${API}/agenda/activities/${id}`, { progresso: value }, { headers: authHeaders() });
      setActivity(res.data);
      toast.success('Progresso atualizado!');
      setDiaryTemplate({ text: `Hoje executei ${value}% dessa atividade. Não concluí porque...`, ts: Date.now() });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar progresso');
    }
  };

  const handleReopen = async () => {
    try {
      await axios.put(`${API}/agenda/activities/${id}/reabrir`, {}, { headers: authHeaders() });
      toast.success('Atividade reaberta!');
      loadActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reabrir atividade');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir esta atividade? Esta ação não pode ser desfeita.')) return;
    try {
      await axios.delete(`${API}/agenda/activities/${id}`, { headers: authHeaders() });
      toast.success('Atividade excluída');
      navigate('/agenda');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir atividade');
    }
  };

  const openEdit = () => {
    setEditForm({
      titulo: activity.titulo, descricao: activity.descricao,
      data_inicio: activity.data_inicio, data_conclusao_prevista: activity.data_conclusao_prevista,
      prioridade: activity.prioridade, categoria: activity.categoria || 'outros',
      lembrete_ativo: activity.lembrete_ativo || false,
      whatsapp_lembrete_ativo: activity.whatsapp_lembrete_ativo || false,
      horario_compromisso: activity.horario_compromisso || '08:00',
      lembrete_antecedencia: activity.lembrete_antecedencia || 'no_momento',
      lembrete_antecedencia_2: activity.lembrete_antecedencia_2 || null,
      fcm_token: activity.fcm_token || null,
    });
    setShowEdit(true);
  };

  useEffect(() => {
    if (!showEdit) return;
    (async () => {
      const token = await requestFcmToken();
      setEditForm(prev => ({ ...prev, lembrete_ativo: !!token, fcm_token: token || null, whatsapp_lembrete_ativo: !!trialInfo?.whatsapp }));
    })();
  }, [showEdit, trialInfo]);

  const handleSaveEdit = async () => {
    if ((editForm.lembrete_ativo || editForm.whatsapp_lembrete_ativo) && !editForm.horario_compromisso) { toast.error('Defina o horário do compromisso'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/agenda/activities/${id}`, editForm, { headers: authHeaders() });
      setActivity(res.data);
      toast.success('Atividade atualizada!');
      setShowEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar atividade');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !activity) {
    return (
      <div className="min-h-screen bg-[#EEF2FF] flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sc = STATUS_LABEL[activity.status] || STATUS_LABEL.pendente;
  const editableDiary = activity.status !== 'concluida';

  return (
    <div className="min-h-screen bg-[#EEF2FF] p-5 space-y-6 pb-10 max-w-3xl mx-auto font-manrope">
      <button onClick={() => navigate('/agenda')} className="flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl text-sm font-medium transition-colors">
        <ArrowLeft size={15} /> Voltar para Agenda
      </button>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-outfit font-light text-gray-900">{activity.titulo}</h1>
              <span className={`text-xs px-2 py-1 rounded-full ${sc.color}`}>{sc.label}</span>
              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500">{PRIORIDADE_LABEL[activity.prioridade]}</span>
            </div>
            <p className="text-sm text-gray-500 mt-2 whitespace-pre-wrap">{activity.descricao}</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            {activity.status !== 'concluida' && (
              <button
                onClick={openEdit}
                data-testid="btn-salvar-atividade-header"
                className="flex items-center gap-1.5 bg-[#1E3A8A] hover:bg-[#172554] text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors"
              >
                <Edit2 size={14} /> Salvar Atividade
              </button>
            )}
            {activity.status !== 'concluida' && (
              <button
                onClick={() => setShowConfirmComplete(true)}
                data-testid="btn-concluir-atividade"
                className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors"
              >
                <Check size={14} /> Concluir
              </button>
            )}
            {activity.status === 'concluida' && (
              <button
                onClick={handleReopen}
                data-testid="btn-reabrir-atividade"
                className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-300 text-gray-700 text-xs font-semibold px-3 py-2 rounded-full transition-colors"
              >
                Reabrir
              </button>
            )}
            <button onClick={handleDelete} className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-full transition-colors">
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border-t border-gray-200 pt-4">
          <div><p className="text-xs text-gray-500">Início</p><p className="text-gray-900 text-[16.8px] font-bold">{fmtDate(activity.data_inicio)}</p></div>
          <div><p className="text-xs text-gray-500">Conclusão Prevista</p><p className="text-gray-900 text-[16.8px] font-bold">{fmtDate(activity.data_conclusao_prevista)}</p></div>
          <div><p className="text-xs text-gray-500">Horário do Compromisso</p><p className="text-gray-900 text-[16.8px] font-bold">{activity.horario_compromisso || '—'}</p></div>
          <div><p className="text-xs text-gray-500">Conclusão Real</p><p className="text-gray-900">{activity.data_conclusao_real ? fmtDateTime(activity.data_conclusao_real) : '—'}</p></div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-500 mb-2">Progresso</p>
          <AgendaProgressBar
            value={activity.progresso || 0}
            editable={activity.status !== 'concluida'}
            onChange={handleProgressChange}
          />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Diário de Atividades</h2>
        {hasMore && (
          <button onClick={loadMore} disabled={loadingMore} className="text-xs text-[#1E3A8A] hover:text-[#172554] disabled:opacity-50">
            {loadingMore ? 'Carregando...' : 'Carregar registros anteriores'}
          </button>
        )}
        {diary.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum registro de diário ainda.</p>
        ) : (
          <div className="space-y-3">
            {diary.map(entry => (
              <AgendaDiaryCard
                key={entry.id}
                entry={entry}
                editable={editableDiary}
                onSave={handleSaveDiary}
                onUpload={handleUpload}
                templateToInsert={entry.data_referencia === todayStr() ? diaryTemplate : null}
              />
            ))}
          </div>
        )}
      </div>

      {showConfirmComplete && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-gray-300 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h2 className="text-lg font-outfit font-light text-gray-900 mb-2">Concluir atividade?</h2>
            <p className="text-sm text-gray-500 mb-3">
              Você tem certeza que concluiu esse compromisso? Se você quer só salvar a agenda do dia, é no botão <strong>Salvar registro</strong> (abaixo da sua descrição, no diário) que deve clicar. Confira se não está clicando em Concluir pensando que está clicando em salvar.
            </p>
            <p className="text-xs text-gray-500/70 mb-5">As linhas de diário futuras não serão mais geradas.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowConfirmComplete(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-300 rounded-full transition-colors">
                Cancelar
              </button>
              <button onClick={handleComplete} data-testid="btn-confirmar-conclusao" className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold rounded-full transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && editForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-gray-300 rounded-2xl p-6 w-full max-w-lg shadow-2xl my-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-outfit font-light text-gray-900">Editar Atividade</h2>
              <button onClick={() => setShowEdit(false)} className="text-gray-500 hover:text-gray-900"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Título</label>
                <input
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                  value={editForm.titulo} onChange={e => setEditForm({ ...editForm, titulo: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Descrição</label>
                <textarea
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A] min-h-[80px] resize-y"
                  value={editForm.descricao} onChange={e => setEditForm({ ...editForm, descricao: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Data de Início</label>
                  <input
                    type="date" className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                    value={editForm.data_inicio} onChange={e => setEditForm({ ...editForm, data_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Data de Conclusão</label>
                  <input
                    type="date" className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                    value={editForm.data_conclusao_prevista} onChange={e => setEditForm({ ...editForm, data_conclusao_prevista: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Prioridade</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                      <input
                        data-testid={`radio-prioridade-edit-${k}`}
                        type="radio"
                        name="prioridade-edit"
                        value={k}
                        checked={editForm.prioridade === k}
                        onChange={() => setEditForm({ ...editForm, prioridade: k })}
                        className="w-4 h-4 accent-[#1E3A8A]"
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Categoria</label>
                <select
                  className="w-full bg-[#EEF2FF] border border-gray-300 rounded-xl px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-[#1E3A8A]/60"
                  value={editForm.categoria} onChange={e => setEditForm({ ...editForm, categoria: e.target.value })}
                >
                  {Object.entries(CATEGORIA_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="border-t border-gray-300 pt-3 space-y-3">
                <div className="flex items-center justify-between text-sm text-gray-900">
                  <span>Lembrete por notificação</span>
                  <span className="text-xs text-gray-500">{editForm.lembrete_ativo ? 'Ativado' : 'Bloqueado pelo navegador'}</span>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-900">
                  <span>Avisar por WhatsApp</span>
                  <span className="text-xs text-gray-500">{editForm.whatsapp_lembrete_ativo ? 'Ativado' : 'Cadastre seu WhatsApp'}</span>
                </div>
                {!trialInfo?.whatsapp && (
                  <p className="text-xs text-gray-500">
                    Cadastre seu WhatsApp no menu (ícone ☰) → "Cadastrar Whatsapp" para ativar este aviso.
                  </p>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Horário do compromisso</label>
                    <div className="flex gap-2">
                      <select
                        data-testid="select-horario-hora-edit"
                        className="flex-1 bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                        value={editForm.horario_compromisso.split(':')[0]}
                        onChange={e => setEditForm({ ...editForm, horario_compromisso: `${e.target.value}:${editForm.horario_compromisso.split(':')[1]}` })}
                      >
                        {Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')).map(h => <option key={h} value={h}>{h}h</option>)}
                      </select>
                      <select
                        data-testid="select-horario-minuto-edit"
                        className="flex-1 bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                        value={editForm.horario_compromisso.split(':')[1]}
                        onChange={e => setEditForm({ ...editForm, horario_compromisso: `${editForm.horario_compromisso.split(':')[0]}:${e.target.value}` })}
                      >
                        {['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}min</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Antecedência do lembrete</label>
                    <div className="flex flex-col gap-2">
                      {Object.entries(ANTECEDENCIA_LABEL).map(([k, v]) => (
                        <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                          <input
                            data-testid={`radio-antecedencia-edit-${k}`}
                            type="radio"
                            name="lembrete_antecedencia-edit"
                            value={k}
                            checked={editForm.lembrete_antecedencia === k}
                            onChange={() => setEditForm({
                              ...editForm,
                              lembrete_antecedencia: k,
                              lembrete_antecedencia_2: k === '1dia' ? (editForm.lembrete_antecedencia_2 || 'no_momento') : null,
                            })}
                            className="w-4 h-4 accent-[#1E3A8A]"
                          />
                          {v}
                        </label>
                      ))}
                    </div>
                  </div>
                  {editForm.lembrete_antecedencia === '1dia' && (
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Segundo lembrete (mais próximo do horário)</label>
                      <div className="flex flex-col gap-2">
                        {Object.entries(ANTECEDENCIA_LABEL_2).map(([k, v]) => (
                          <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                            <input
                              data-testid={`radio-antecedencia-2-edit-${k}`}
                              type="radio"
                              name="lembrete_antecedencia_2-edit"
                              value={k}
                              checked={editForm.lembrete_antecedencia_2 === k}
                              onChange={() => setEditForm({ ...editForm, lembrete_antecedencia_2: k })}
                              className="w-4 h-4 accent-[#1E3A8A]"
                            />
                            {v}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowEdit(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-300 rounded-full transition-colors">Cancelar</button>
              <button
                onClick={handleSaveEdit} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full font-medium text-sm transition-colors disabled:opacity-50"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={15} />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
